import { createFileRoute } from '@tanstack/react-router';
import { Check, X } from "lucide-react";
import { ContentHeader, ContentSection, FAQSection, CTASection } from "@/components/content";

export const Route = createFileRoute('/comparisons/langchain')({
  component: LangChainComparisonPage,
  head: () => ({
    meta: [
      { title: 'Matricks vs LangChain | Matricks' },
      { name: 'description', content: 'Compare Matricks AI Agents Platform with LangChain. Visual builder vs code-first approach for building AI agents.' },
    ],
  }),
});

const comparisonData = [
  { feature: "AI Agent Creation", matricks: true, langchain: true },
  { feature: "Tool Integration", matricks: true, langchain: true },
  { feature: "Multiple LLM Support", matricks: true, langchain: true },
  { feature: "Memory Management", matricks: true, langchain: true },
  { feature: "No-Code Builder", matricks: true, langchain: false },
  { feature: "Visual Agent Design", matricks: true, langchain: false },
  { feature: "Managed Deployment", matricks: true, langchain: false },
  { feature: "Built-in Monitoring", matricks: true, langchain: false },
  { feature: "2000+ Pre-built Tools", matricks: true, langchain: "Community" },
  { feature: "Enterprise Security", matricks: true, langchain: "DIY" },
  { feature: "Multi-Agent Orchestration", matricks: true, langchain: true },
];

const faqItems = [
  {
    question: "Can I use LangChain with Matricks?",
    answer: "You can use LangChain agents alongside Matricks, though you might not need to. Matricks provides similar agent capabilities with a visual builder and managed infrastructure, making LangChain unnecessary for most use cases.",
  },
  {
    question: "Is Matricks suitable for developers?",
    answer: "Absolutely. While Matricks offers no-code options, developers can also work with code, export agent configurations, and extend functionality programmatically. It's designed to be accessible without sacrificing power.",
  },
  {
    question: "How does agent performance compare?",
    answer: "Both can create high-performing agents. Matricks handles infrastructure optimization automatically. With LangChain, you're responsible for hosting, scaling, and optimization. For production workloads, Matricks typically delivers better out-of-the-box performance.",
  },
];

function LangChainComparisonPage() {
  return (
    <div>
      <ContentHeader
        title="Matricks vs LangChain"
        description="LangChain is a popular open-source framework for building AI agents. Here's how Matricks compares with our managed platform approach."
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Comparisons", href: "/comparisons" },
          { label: "vs LangChain" },
        ]}
      />

      {/* Overview */}
      <ContentSection id="overview" title="Overview">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold text-lg">LangChain</h3>
            <p className="text-muted-foreground mt-2">
              LangChain is an open-source Python/JavaScript framework for building AI agents.
              It provides abstractions for chains, agents, and tools. Best for developers
              who want maximum flexibility and are comfortable managing their own infrastructure.
            </p>
          </div>
          <div className="rounded-xl border bg-primary/5 border-primary/20 p-6">
            <h3 className="font-semibold text-lg">Matricks</h3>
            <p className="text-muted-foreground mt-2">
              Matricks is a managed AI agent platform with visual builder and 2000+ pre-built
              integrations. Create, deploy, and monitor agents without managing infrastructure.
              Best for teams wanting production-ready agents without extensive development.
            </p>
          </div>
        </div>
      </ContentSection>

      {/* Comparison Table */}
      <ContentSection
        id="comparison"
        title="Feature Comparison"
        background="muted"
      >
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b">
                <th className="py-4 px-4 text-left font-semibold">Feature</th>
                <th className="py-4 px-4 text-center font-semibold bg-primary/5 text-primary">Matricks</th>
                <th className="py-4 px-4 text-center font-semibold">LangChain</th>
              </tr>
            </thead>
            <tbody>
              {comparisonData.map((row) => (
                <tr key={row.feature} className="border-b last:border-0">
                  <td className="py-4 px-4">{row.feature}</td>
                  <td className="py-4 px-4 text-center bg-primary/5">
                    {row.matricks === true ? (
                      <Check className="h-5 w-5 text-green-500 mx-auto" />
                    ) : row.matricks === false ? (
                      <X className="h-5 w-5 text-red-500 mx-auto" />
                    ) : (
                      <span className="text-sm">{row.matricks}</span>
                    )}
                  </td>
                  <td className="py-4 px-4 text-center">
                    {row.langchain === true ? (
                      <Check className="h-5 w-5 text-green-500 mx-auto" />
                    ) : row.langchain === false ? (
                      <X className="h-5 w-5 text-red-500 mx-auto" />
                    ) : (
                      <span className="text-sm text-muted-foreground">{row.langchain}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ContentSection>

      {/* Key Differences */}
      <ContentSection id="differences" title="Key Differences">
        <div className="space-y-6">
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold">Build vs Buy</h3>
            <p className="text-muted-foreground mt-2">
              LangChain is a framework—you build everything yourself. Matricks is a platform—you
              configure and deploy. LangChain offers maximum flexibility; Matricks offers
              faster time-to-value with less engineering effort.
            </p>
          </div>
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold">Infrastructure</h3>
            <p className="text-muted-foreground mt-2">
              With LangChain, you're responsible for hosting, scaling, monitoring, and securing
              your agents. Matricks is fully managed—we handle all operational concerns so you
              can focus on agent behavior.
            </p>
          </div>
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold">Tool Ecosystem</h3>
            <p className="text-muted-foreground mt-2">
              LangChain has community tools that require integration work. Matricks provides
              2000+ pre-built integrations ready to use. Connecting an agent to your CRM or
              database is one click, not custom code.
            </p>
          </div>
        </div>
      </ContentSection>

      {/* When to Choose */}
      <ContentSection
        id="when-to-choose"
        title="When to Choose Each"
        background="muted"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold mb-4">Choose LangChain When:</h3>
            <ul className="space-y-2 text-muted-foreground">
              <li>• You need maximum flexibility and customization</li>
              <li>• You have strong AI/ML engineering expertise</li>
              <li>• You want to own and manage all infrastructure</li>
              <li>• You're building highly novel agent architectures</li>
            </ul>
          </div>
          <div className="rounded-xl border bg-primary/5 border-primary/20 p-6">
            <h3 className="font-semibold mb-4">Choose Matricks When:</h3>
            <ul className="space-y-2 text-muted-foreground">
              <li>• You want agents in production quickly</li>
              <li>• You prefer visual/no-code configuration</li>
              <li>• You need pre-built integrations with business tools</li>
              <li>• You want managed infrastructure and monitoring</li>
              <li>• Enterprise security and compliance are required</li>
            </ul>
          </div>
        </div>
      </ContentSection>

      {/* FAQ */}
      <FAQSection items={faqItems} />

      {/* CTA */}
      <CTASection
        title="Build Agents Without the Complexity"
        description="Try Matricks and deploy your first AI agent in minutes."
        primaryAction={{ label: "Start Building", href: "/login" }}
        secondaryAction={{ label: "View Agent Platform", href: "/features/agents-platform" }}
        background="primary"
      />
    </div>
  );
}
